import type { VitePreloadErrorEvent } from "../types/vite"

interface PreloadErrorRouter {
  onError(handler: (error: Error, to: { fullPath: string }) => void): void
}

export interface VitePreloadErrorContext {
  error: Error
  message: string
  currentPath: string
  timestamp: string
  userAgent: string
}

export interface VitePreloadErrorOptions {
  onError?: (context: VitePreloadErrorContext) => void
  router?: PreloadErrorRouter
}

export function initializeVitePreloadErrorHandler(
  options: VitePreloadErrorOptions = {}
): () => void {
  const { onError, router } = options

  function handlePreloadError(event: Event): void {
    event.preventDefault()

    const preloadEvent = event as VitePreloadErrorEvent // https://github.com/vitejs/vite/commit/2eca54e
    const error = preloadEvent.payload

    trackedPreloadErrors.add(error)

    const context: VitePreloadErrorContext = {
      error,
      message: error.message,
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    }

    console.error("[vite:preloadError] stale chunk detected", context)

    onError?.(context)

    if (canReload()) {
      const fallbackTimeout = setTimeout(() => {
        trackedPreloadErrors.delete(error)
        pendingReloads.delete(error)
        recordReloadAndNavigate(window.location.href)
      }, 3000)
      pendingReloads.set(error, fallbackTimeout)
    } else {
      console.warn("[vite:preloadError] reload threshold exceeded, skipping auto-reload")
    }
  }

  if (router) {
    router.onError((error, to) => {
      if (!trackedPreloadErrors.has(error) && !isChunkLoadError(error)) {
        return
      }

      trackedPreloadErrors.delete(error)

      const timeout = pendingReloads.get(error)
      if (timeout) {
        clearTimeout(timeout)
        pendingReloads.delete(error)
      }

      if (canReload()) {
        recordReloadAndNavigate(to.fullPath)
      } else {
        console.warn("[vite:preloadError] reload threshold exceeded, skipping auto-reload")
      }
    })
  }

  // see https://vite.dev/guide/build#load-error-handling
  window.addEventListener("vite:preloadError", handlePreloadError)
  return () => window.removeEventListener("vite:preloadError", handlePreloadError)
}

const CHUNK_LOAD_ERROR_PATTERNS = [
  "Couldn't resolve component",
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
  "Loading chunk",
  "Loading CSS chunk",
]

function isChunkLoadError(error: Error): boolean {
  const message = error.message
  if (!message) {
    return false
  }
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

const RELOAD_COUNT_KEY = "vite-preload-reload-count"
const RELOAD_COOLDOWN_MS = 30_000
const MAX_RELOADS = 1

const trackedPreloadErrors = new Set<Error>()
const pendingReloads = new Map<Error, ReturnType<typeof setTimeout>>()

interface ReloadState {
  count: number
  timestamp: number
}

function getReloadState(): ReloadState {
  try {
    const raw = sessionStorage.getItem(RELOAD_COUNT_KEY)
    if (raw) {
      return JSON.parse(raw) as ReloadState
    }
  } catch {
    // corrupted state — treat as fresh
  }
  return { count: 0, timestamp: Date.now() }
}

function setReloadState(state: ReloadState): void {
  sessionStorage.setItem(RELOAD_COUNT_KEY, JSON.stringify(state))
}

function canReload(): boolean {
  const state = getReloadState()
  const now = Date.now()

  if (now - state.timestamp > RELOAD_COOLDOWN_MS) {
    // Cooldown expired — reset and allow
    return true
  }

  return state.count < MAX_RELOADS
}

function recordReloadAndNavigate(url: string): void {
  try {
    const state = getReloadState()
    const now = Date.now()

    if (now - state.timestamp > RELOAD_COOLDOWN_MS) {
      setReloadState({ count: 1, timestamp: now })
    } else {
      setReloadState({ count: state.count + 1, timestamp: state.timestamp })
    }
  } catch {
    // storage full or unavailable — proceed with reload anyway
  }

  window.location.href = url
}
